import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IPatient, IPersonalData, ReduxStore } from '../../../constants/types';
import { Bill, BillItem, DocumentStatus, DocumentType } from './types';
import { Translate, withLocalize, LocalizeContextProps } from 'react-localize-redux';
import { BillForm } from './BillForm';
import { FindPatient } from './find_patient';
import { useStatusDocument } from '../../../hooks';
import PatientInfo from './PatientInfo';
import { Alert, AlertTitle, Box, Card, FormControl, FormControlLabel, FormLabel, Grid, Radio,
        RadioGroup, Typography } from '@mui/material';
import ReactDOMServer from 'react-dom/server';
import { fetchPatient } from '../../../db';

interface BillCreateProps {
    patients:IPatient[],
    personalFields:IPersonalData[],
    currency: string,
    uuidInvestigation: string,
    idBillingInfo:number,
    canCreateBugdet: boolean,
    languageCode: string,
    surveyAdditionalInfo?: any,
    withDiscount: boolean,
    onCreateBill: (bill: Bill) => void,
    onCancelBill: () => void

}

const BillCreate: React.FC<BillCreateProps & LocalizeContextProps> = (props) => {
    const [patient, setPatient] = useState<null | IPatient>(null);
    // SHER-460 #11: a new blank bill defaults to "Facture définitive" (INVOICE) + "Modifiable"
    // (DRAFT, from useStatusDocument below) — the most common manual-billing case for CHOM staff.
    // "Synthèse des actes" (SUMMARY) stays available as a choice when budgets are allowed, but is
    // no longer the default.
    const [typeDocument, setTypeDocument] = useState<DocumentType>(DocumentType.INVOICE);
    const {statusDocument, renderStatusDocument} = useStatusDocument(DocumentStatus.DRAFT);
    const { translate } = props;
    const location = useLocation();

    function onCreateBill(billItems: BillItem[]) {
        const bill: Bill = {} as Bill;
        bill.type = typeDocument;
        bill.status = statusDocument;
        bill.billItems = billItems;
        bill.uuidPatient = patient!.uuid;
        props.onCreateBill(bill);
    }

    async function onPatientSelected(uuidPatient: string) {
        const fetchedPatient = await fetchPatient(uuidPatient);
        if (fetchedPatient) {
            setPatient(fetchedPatient);
        }
    }

    // SHER-143 — when the user clicks the "+" button on a patient's bills page,
    // the parent forwards the uuid via ?patient=<uuid>. Pre-select the patient
    // so the user doesn't have to search for them again mid-flow.
    useEffect(() => {
        const queryUuid = new URLSearchParams(location.search).get('patient');
        if (queryUuid && !patient) {
            onPatientSelected(queryUuid);
        }
    }, [location.search]);

    const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTypeDocument(Number((event.target as HTMLInputElement).value));
    };
    
    function renderTypeDocument(){
        if(props.canCreateBugdet && patient){
            return (
                <FormControl>
                  <FormLabel id="demo-row-radio-buttons-group-label"><Typography variant="body2" component='span' fontWeight='bold' ><Translate id="hospital.billing.bill.type" /></Typography></FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={typeDocument}
                    onChange={handleChangeType}
                  >
                    <FormControlLabel value={DocumentType.SUMMARY} control={<Radio />} label={<Typography variant="body2" component='span' ><Translate id="hospital.billing.bill.types.summary" /></Typography>} />
                    <FormControlLabel value={DocumentType.INVOICE} control={<Radio />} label={<Typography variant="body2" component='span' ><Translate id="hospital.billing.bill.types.invoice" /></Typography>} />
                  </RadioGroup>
                </FormControl>
              );
        }
        else{
            return null;
        }
    }

    function renderOptions(){
        if(patient){
            return (
                <Card style={{margin:'10px'}}>
                    <Box padding={2} > 
                        { renderTypeDocument() }
                        { renderStatusDocument() }
                    </Box>
                </Card>
            )
        }
        else{
            return null;
        }
        
    }

    function renderPatient(){
        if(!patient){
            return <FindPatient uuidInvestigation={props.uuidInvestigation}
                        personalFields={props.personalFields}
                        codeLanguage={props.languageCode}
                        onPatientSelected={(uuidPatient) => onPatientSelected(uuidPatient)} />
        }
        else{
            return (
                <Card style={{margin:'10px'}}>
                    <Box padding={2} > 
                    <PatientInfo patient={patient} languageCode={props.languageCode} />
                    </Box>
                </Card>
            )
        }
    }

    if(patient && patient.personalData["coverage"] === "") {
        let insuranceMessage = translate('hospital.billing.bill.error.no_coverage', { X: '/path/to/insurance/add' }).toString();
        insuranceMessage = insuranceMessage.replace("%X", `/patient/${patient.uuid}`);
        return (
            <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                <strong dangerouslySetInnerHTML={{ __html: insuranceMessage }} />
            </Alert>
        )
    }

    return (
        <>
            {
                renderPatient()
            }
            {
                renderOptions()
            }
            
            <BillForm patient={patient!} canUpdateBill={true} currency={props.currency} idBillingInfo={props.idBillingInfo}
                withDiscount={props.withDiscount} uuidInvestigation={props.uuidInvestigation} bill={null} 
                print={false}
                onBillItemsValidated={onCreateBill} 
                                        onUpdateBillItemStatus={(billItems, paymentAmount) => console.log("Bill items status updated:", billItems, "Payment amount:", paymentAmount)}
                onCancelBill={props.onCancelBill}
                />
        </>
    );
};

export default BillCreate;
